import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { Box, Button, Input, Heading, useToast } from '@chakra-ui/react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
      setCategories(response.data.$values || response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddCategory = async () => {
    if (!categoryName) {
      toast({
        title: 'Category name is required',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/categories`, { categoryName });
      setCategoryName('');
      fetchCategories();
      toast({
        title: 'Category added successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryName || selectedCategoryId === null) {
      toast({
        title: 'Category name and ID are required',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/categories/${selectedCategoryId}`, { 
        categoryId: selectedCategoryId, 
        categoryName 
      });
      setCategoryName('');
      setSelectedCategoryId(null);
      fetchCategories();
      toast({
        title: 'Category modified successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error modifying category:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/categories/${id}`);
        fetchCategories(); // Refetch categories after deletion
        toast({
          title: 'Category deleted successfully',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const handleEdit = (category) => {
    setSelectedCategoryId(category.categoryId);
    setCategoryName(category.categoryName);
  };

  const columnDefs = useMemo(() => [
    { headerName: 'S.No', valueGetter: 'node.rowIndex + 1', sortable: true, filter: true },
    { headerName: 'Category Name', field: 'categoryName', sortable: true, filter: true },
    {
      headerName: 'Actions',
      field: 'categoryId',
      cellRenderer: (params) => (
        <div>
          <Button size="xs" colorScheme="blue" onClick={() => handleEdit(params.data)} mr={2}>Modify</Button>
          <Button size="xs" colorScheme="red" onClick={() => handleDelete(params.value)}>Delete</Button>
        </div>
      ),
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    minWidth: 100,
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true,
  }), []);

  return (
    <Box p={8}>
      <Heading mb={6}>Category Manager</Heading>
      <Box mb={4}>
        <Input
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
          placeholder="Category Name"
          mb={4}
        />
        {selectedCategoryId !== null ? (
          <Button onClick={handleSaveCategory} colorScheme="blue">Save Category</Button>
        ) : (
          <Button onClick={handleAddCategory} colorScheme="blue">Add Category</Button>
        )}
      </Box>
      <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={categories}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={10}
        />
      </div>
    </Box>
  );
};

export default CategoryManager;
